import type {
  CSSProperties,
  AnchorHTMLAttributes,
  FunctionComponent,
} from 'react';
import React, { useEffect } from 'react';
import cx from 'classnames';
import styles from './Card.less';
import badgeStyles from './Badge.less';
import avatarStyles from '../avatar/Avatar.less';
import { UnsplashTracker } from '@trello/unsplash';
import {
  smallestPreviewBiggerThan,
  biggestPreview,
} from '@trello/image-previews';
import { isHighDPI } from '@trello/browser';

type CoverColor =
  | 'blue'
  | 'green'
  | 'orange'
  | 'purple'
  | 'red'
  | 'yellow'
  | 'pink'
  | 'sky'
  | 'lime'
  | 'black';

export interface ImagePreviewModel {
  scaled: boolean;
  height: number;
  url: string;
  width: number;
}

interface CoverPhotoModel {
  url?: string;
  edgeColor?: string | null;
  previews?: ImagePreviewModel[];
  sharedSourceUrl?: string;
  color?: CoverColor | null;
  size?: 'normal' | 'full';
  brightness?: 'light' | 'dark';
}

export const colorCoverColorBlindPattern: {
  [key in CoverColor]:
    | 'stripe-diagonal'
    | 'wave-square'
    | 'stripe-vertical'
    | 'dots-checker'
    | 'groove-diagonal'
    | 'stripe-horizontal'
    | 'brick-horizontal'
    | 'brick-diagonal'
    | 'scale'
    | 'brick-vertical';
} = {
  red: 'dots-checker',
  blue: 'stripe-horizontal',
  green: 'stripe-diagonal',
  orange: 'stripe-vertical',
  purple: 'groove-diagonal',
  yellow: 'wave-square',
  pink: 'brick-horizontal',
  sky: 'brick-diagonal',
  lime: 'scale',
  black: 'brick-vertical',
};

function getFullColorCoverClasses(color: CoverColor | null | undefined) {
  return {
    [styles.colorCardCoverBlue]: color === 'blue',
    [styles.colorCardCoverGreen]: color === 'green',
    [styles.colorCardCoverOrange]: color === 'orange',
    [styles.colorCardCoverRed]: color === 'red',
    [styles.colorCardCoverPurple]: color === 'purple',
    [styles.colorCardCoverYellow]: color === 'yellow',
    [styles.colorCardCoverPink]: color === 'pink',
    [styles.colorCardCoverSky]: color === 'sky',
    [styles.colorCardCoverLime]: color === 'lime',
    [styles.colorCardCoverBlack]: color === 'black',
  };
}

function getColorCoverColorBlindPatternClasses(
  color: CoverColor | null | undefined,
) {
  const pattern = colorCoverColorBlindPattern[color as CoverColor];

  switch (pattern) {
    case 'stripe-diagonal':
      return styles['stripe-diagonal'];
    case 'wave-square':
      return styles['wave-square'];
    case 'stripe-vertical':
      return styles['stripe-vertical'];
    case 'dots-checker':
      return styles['dots-checker'];
    case 'groove-diagonal':
      return styles['groove-diagonal'];
    case 'stripe-horizontal':
      return styles['stripe-horizontal'];
    case 'brick-horizontal':
      return styles['brick-horizontal'];
    case 'brick-diagonal':
      return styles['brick-diagonal'];
    case 'scale':
      return styles['scale'];
    case 'brick-vertical':
      return styles['brick-vertical'];
    default:
      return undefined;
  }
}

function getFullCoverClassesAndStyle({
  cover,
  colorBlind,
  truncateNameForFullCover,
  coverHeight,
  hasStickers,
}: {
  cover: CoverPhotoModel | null | undefined;
  colorBlind: boolean | undefined;
  truncateNameForFullCover?: boolean;
  coverHeight?: number;
  hasStickers?: boolean;
}) {
  let classNames: string | undefined = undefined;
  const style: React.CSSProperties = {};

  const { color, size, brightness } = cover || {};
  const hasFullCover = size === 'full';
  const hasFullColorCover = Boolean(hasFullCover && color);
  const preview = cover
    ? smallestPreviewBiggerThan(cover.previews, isHighDPI() ? 600 : 300) ||
      biggestPreview(cover.previews)
    : null;

  if (hasFullCover && preview) {
    style.backgroundImage = `url("${preview.url}")`;
    style.backgroundSize = 'cover';

    const maxHeight = Math.min(
      preview.height,
      coverHeight ? coverHeight * 2 : 260,
    );
    const calculatedHeight = (preview.height * 245) / preview.width;

    if (calculatedHeight <= maxHeight) {
      style.height = calculatedHeight;
    } else {
      style.height = maxHeight;
    }

    classNames = cx(
      styles.fullCover,
      styles.fullImageCover,
      brightness === 'dark' && styles.fullCoverDark,
      truncateNameForFullCover && styles.truncateNameForFullCover,
    );
  } else if (hasFullColorCover) {
    classNames = cx(
      {
        [styles.fullCover]: hasFullCover,
        [styles.fullCoverColorBlind]: hasFullColorCover && colorBlind,
        [styles.fullCoverDark]: brightness === 'dark',
        ...(hasFullColorCover ? getFullColorCoverClasses(color) : {}),
        [styles.fullColorCoverWithStickers]: hasStickers,
      },
      hasFullColorCover &&
        colorBlind &&
        getColorCoverColorBlindPatternClasses(color),
      truncateNameForFullCover && styles.truncateNameForFullCover,
    );
  }

  return { classNames, style };
}

interface CardProps {
  className?: string;
  style?: CSSProperties;
  cover?: CoverPhotoModel | null;
  colorBlind?: boolean;
  truncateNameForFullCover?: boolean;
  coverHeight?: number;
  hasStickers?: boolean;
}

export const Card: FunctionComponent<CardProps> = ({
  className,
  children,
  style,
  cover,
  colorBlind,
  coverHeight,
  truncateNameForFullCover,
  hasStickers,
}) => {
  const baseCardClasses = cx(className, styles.card);
  const {
    classNames: fullCoverClasses,
    style: fullCoverStyles,
  } = getFullCoverClassesAndStyle({
    cover,
    colorBlind,
    coverHeight,
    truncateNameForFullCover,
    hasStickers,
  });

  return (
    <div
      className={cx(baseCardClasses, fullCoverClasses)}
      style={{
        ...fullCoverStyles,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

export interface LinkComponentProps
  extends CardProps,
    AnchorHTMLAttributes<HTMLAnchorElement> {}

interface CardLinkProps extends CardProps {
  href?: string;
  onClick?: () => void;
  linkComponent?:
    | FunctionComponent<LinkComponentProps>
    | React.ComponentClass<LinkComponentProps>;
}

export const CardLink: FunctionComponent<CardLinkProps> = ({
  linkComponent: LinkComponent,
  className,
  cover,
  truncateNameForFullCover,
  colorBlind,
  coverHeight,
  hasStickers,
  ...props
}) => {
  const baseCardClasses = cx(className, styles.card, styles.cardLink);
  const {
    classNames: fullCoverClasses,
    style: fullCoverStyles,
  } = getFullCoverClassesAndStyle({
    cover,
    colorBlind,
    truncateNameForFullCover,
    hasStickers,
  });

  if (LinkComponent) {
    return (
      <LinkComponent
        className={cx(baseCardClasses, fullCoverClasses)}
        {...props}
        style={{
          ...fullCoverStyles,
          ...props.style,
        }}
      />
    );
  }
  return (
    <a
      className={cx(baseCardClasses, fullCoverClasses)}
      {...props}
      style={{
        ...fullCoverStyles,
        ...props.style,
      }}
    />
  );
};

interface CardStickersProps {
  height?: number;
  className?: string;
}

export const CardStickers: FunctionComponent<CardStickersProps> = ({
  height,
  children,
  className,
}) => (
  <div
    className={cx(className, styles.stickers)}
    style={height ? { height: `${height}px` } : undefined}
  >
    {children}
  </div>
);

type ColorCoverColorBlindPattern =
  | 'stripe-diagonal'
  | 'wave-square'
  | 'stripe-vertical'
  | 'dots-checker'
  | 'groove-diagonal'
  | 'stripe-horizontal'
  | 'brick-horizontal'
  | 'brick-diagonal'
  | 'scale'
  | 'brick-vertical';

interface CardCoverProps {
  bgColor?: string | null;
  img?: string;
  bgSize?: 'cover' | 'contain';
  height?: number;
  sharedSourceUrl?: string;
  pattern?: ColorCoverColorBlindPattern | null;
  previews?: ImagePreviewModel[];
  size?: string;
  colorBlind?: boolean;
}

export const CardCover: FunctionComponent<CardCoverProps> = ({
  img,
  sharedSourceUrl,
  height,
  bgSize,
  bgColor,
  pattern,
  children,
  previews,
  size,
  colorBlind,
}) => {
  useEffect(() => {
    if (sharedSourceUrl) {
      UnsplashTracker.trackOncePerInterval(sharedSourceUrl);
    }
  }, [sharedSourceUrl]);

  const dynamicStyles: CSSProperties = {};

  if (height) {
    dynamicStyles.height = `${height}px`;
  }

  if (img) {
    dynamicStyles.backgroundImage = `url(${img})`;
  }

  if (bgColor) {
    dynamicStyles.backgroundColor = bgColor;
  }

  if (bgSize) {
    dynamicStyles.backgroundSize = bgSize;
  }

  if (previews) {
    const hasFullCover = size === 'full';
    const preview =
      smallestPreviewBiggerThan(previews, isHighDPI() ? 600 : 300) ||
      biggestPreview(previews);

    if (preview && !hasFullCover) {
      dynamicStyles.backgroundImage = `url("${preview.url}")`;
      dynamicStyles.backgroundSize = 'cover';

      const calculatedHeight = (preview.height * 245) / preview.width;
      const maxHeight = Math.min(preview.height, 260); // An arbitrarily chosen number

      if (calculatedHeight <= maxHeight) {
        dynamicStyles.height = calculatedHeight;
      } else {
        dynamicStyles.height = maxHeight;
      }
    }
  }

  let colorBlindPattern = pattern;
  if (!colorBlindPattern && colorBlind) {
    colorBlindPattern = colorCoverColorBlindPattern[bgColor as CoverColor];
  }

  const coverClasses = cx(
    styles.cover,
    pattern === 'stripe-diagonal' && styles['stripe-diagonal'],
    pattern === 'wave-square' && styles['wave-square'],
    pattern === 'stripe-vertical' && styles['stripe-vertical'],
    pattern === 'dots-checker' && styles['dots-checker'],
    pattern === 'groove-diagonal' && styles['groove-diagonal'],
    pattern === 'stripe-horizontal' && styles['stripe-horizontal'],
    pattern === 'brick-horizontal' && styles['brick-horizontal'],
    pattern === 'brick-diagonal' && styles['brick-diagonal'],
    pattern === 'scale' && styles['scale'],
    pattern === 'brick-vertical' && styles['brick-vertical'],
  );

  return (
    <div className={coverClasses} style={dynamicStyles}>
      {children}
    </div>
  );
};

export const CardDetails: FunctionComponent<{ className?: string }> = ({
  children,
  className,
}) => <div className={cx(className, styles.details)}>{children}</div>;

export const CardTitle: FunctionComponent<{ className?: string }> = ({
  className,
  children,
}) => <div className={cx(className, styles.title)}>{children}</div>;

interface CardLabelsProps {
  className?: string;
  title?: string;
}

export const CardLabels: FunctionComponent<CardLabelsProps> = ({
  children,
  className,
  title,
}) => (
  <div title={title} className={cx(styles.labels, className)}>
    {children}
  </div>
);

export const CardBadges: FunctionComponent<{ className?: string }> = ({
  children,
  className,
}) => (
  <div className={cx(className, styles.badges, badgeStyles.cardFront)}>
    {children}
  </div>
);

export const CardMembers: FunctionComponent<{ className?: string }> = ({
  children,
  className,
}) => (
  <div className={cx(className, styles.members, avatarStyles.cardFront)}>
    {children}
  </div>
);
